import { publishLineageUpdated } from "@/lib/lineage/publishLineageUpdated";
import {
  useDomainEventStore,
  type PublishDomainEventInput,
} from "@/stores/useDomainEventStore";
import type { NormalizedResourceEventPayload } from "@/lib/realtime/normalizeResourceEventPayload";
import { handleSourceConnectionStatusChangedEvent } from "@/lib/oauth/handleSourceConnectionStatusChangedEvent";
import { applyConversationLineageEdgeChange } from "@/services/lineageEdgeService";
import { useConversationSuggestionsStore } from "@/stores/useConversationSuggestionsStore";

type ResourceEventHandler = (payload: NormalizedResourceEventPayload) => PublishDomainEventInput | null | void;

const handlers: Record<string, ResourceEventHandler> = {
  documentChangedEvent: (payload) => {
    if (payload.name !== "documentChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "document.changed",
      scope: { projectId: event.projectPublicId },
      entityTypesAffected: ["document"],
      source: "resource-events",
      payload: {
        documentId: event.documentPublicId,
        documentName: event.documentName,
        documentSize: event.documentSize,
        documentType: event.documentType,
        indexingStatus: event.indexingStatus,
      },
    };
  },
  documentIndexingStatusChangedEvent: (payload) => {
    if (payload.name !== "documentIndexingStatusChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "document.indexing_status_changed",
      scope: { projectId: event.projectPublicId },
      entityTypesAffected: ["document"],
      source: "resource-events",
      payload: {
        documentId: event.documentPublicId,
        indexingStatus: event.indexingStatus,
        indexingError: event.indexingError ?? null,
      },
    };
  },
  lineageEdgeChangedEvent: (payload) => {
    if (payload.name !== "lineageEdgeChangedEvent") {
      return;
    }
    const event = payload.event;
    const action = event.action === "ATTACHED" ? "attached" : "detached";
    applyConversationLineageEdgeChange({
      projectId: payload.projectPublicId,
      action,
      edgeId: event.edgePublicId,
      subjectType: event.subjectType,
      subjectPublicId: event.subjectPublicId,
      targetType: event.targetType,
      targetPublicId: event.targetPublicId,
    });
    publishLineageUpdated({
      action,
      edgeId: event.edgePublicId,
      subjectType: event.subjectType,
      subjectPublicId: event.subjectPublicId,
      targetType: event.targetType,
      targetPublicId: event.targetPublicId,
      projectId: payload.projectPublicId,
      source: "resource-events",
    });
  },
  sourceConnectionStatusChangedEvent: (payload) => {
    if (payload.name !== "sourceConnectionStatusChangedEvent") {
      return;
    }
    handleSourceConnectionStatusChangedEvent(payload.event);
  },
  sourceImportBatchChangedEvent: (payload) => {
    if (payload.name !== "sourceImportBatchChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "source.import_batch_changed",
      scope: { projectId: event.projectPublicId },
      entityTypesAffected: ["source_import_batch"],
      source: "resource-events",
      payload: {
        batchId: event.batchPublicId,
        connectionId: event.connectionPublicId,
        batchStatus: event.status,
        totalCount: event.totalCount,
        completedCount: event.completedCount,
      },
    };
  },
  toolResultAppliedEvent: (payload) => {
    if (payload.name !== "toolResultAppliedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "tool_result.applied",
      scope: {
        projectId: payload.projectPublicId,
        conversationId: event.conversationPublicId,
      },
      entityTypesAffected: ["entity_holder"],
      source: "resource-events",
      payload: {
        toolCallId: event.toolCallId,
        toolName: event.toolName,
      },
    };
  },
  conversationMessageChangedEvent: (payload) => {
    if (payload.name !== "conversationMessageChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "conversation.messages_stale",
      scope: {
        projectId: payload.projectPublicId,
        conversationId: event.conversationPublicId,
      },
      entityTypesAffected: ["message"],
      source: "resource-events",
      payload: {
        messageId: event.messagePublicId,
        changeKind: event.changeKind,
      },
    };
  },
  conversationSuggestionsChangedEvent: (payload) => {
    if (payload.name !== "conversationSuggestionsChangedEvent") {
      return;
    }
    useConversationSuggestionsStore
      .getState()
      .applyReplacement(payload.userPublicId, payload.event.suggestions);
  },
  assumptionChangedEvent: (payload) => {
    if (payload.name !== "assumptionChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "assumption.changed",
      scope: {
        projectId: event.projectPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
      entityTypesAffected: ["assumption"],
      source: "resource-events",
      payload: {
        assumptionId: event.assumptionPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
    };
  },
  hypothesisChangedEvent: (payload) => {
    if (payload.name !== "hypothesisChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "hypothesis.changed",
      scope: {
        projectId: event.projectPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
      entityTypesAffected: ["hypothesis"],
      source: "resource-events",
      payload: {
        hypothesisId: event.hypothesisPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
    };
  },
  experimentChangedEvent: (payload) => {
    if (payload.name !== "experimentChangedEvent") {
      return null;
    }
    const event = payload.event;
    return {
      eventType: "experiment.changed",
      scope: {
        projectId: event.projectPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
      entityTypesAffected: ["experiment"],
      source: "resource-events",
      payload: {
        experimentId: event.experimentPublicId,
        conversationId: event.conversationPublicId ?? null,
      },
    };
  },
};

/**
 * Routes a normalized resource-events STOMP payload into the client domain-event bus.
 */
export function routeResourceEventToDomainStore(payload: NormalizedResourceEventPayload): void {
  const handler = handlers[payload.name];
  if (!handler) {
    return;
  }

  const domainEvent = handler(payload);
  if (!domainEvent) {
    return;
  }

  useDomainEventStore.getState().publishDomainEvent(domainEvent);
}
